import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { beliefQuote } from "@/lib/gatewayContent";
import SanctuaryHeader from "@/components/gateway/SanctuaryHeader";
import { X, Zap, Sparkles, Sun, CalendarDays, Compass, Lock } from "lucide-react";

const GRID = [
  { key: "urgent_important", label: "Do now", hint: "Urgent + meaningful" },
  { key: "not_urgent_important", label: "Schedule", hint: "Not urgent, matters" },
  { key: "urgent_not_important", label: "Simplify", hint: "Urgent, draining" },
  { key: "not_urgent_not_important", label: "Let go", hint: "Neither — release it" },
];

const Chip = ({ children }) => <span className="block text-white/80 text-sm bg-white/5 rounded-xl px-3 py-2 mb-2">{children}</span>;

const Readiness = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quote] = useState(() => beliefQuote(user?.belief_filter));

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    api.post("/gateway/momentum").then(({ data }) => setData(data)).catch(() => setData({})).finally(() => setLoading(false));
  }, [user]);

  return (
    <div className="min-h-screen px-5 py-6 relative"
      style={{ background: "radial-gradient(ellipse at 50% 15%, #16302a 0%, #0f2019 55%, #0a1410 100%)" }}
      data-testid="readiness-screen">
      <SanctuaryHeader tone="dark" className="absolute top-5 left-5 z-20" />
      <button onClick={() => navigate("/gateway")} data-testid="readiness-exit" className="absolute top-5 right-5 text-white/25 hover:text-white/60"><X className="w-6 h-6" /></button>

      <div className="max-w-2xl mx-auto">
        <h1 className="font-serif text-4xl text-white text-center mt-2">Momentum</h1>
        <p className="text-white/50 text-center mt-1 mb-8 font-light">Gently shaped from your own words.</p>

        {!user && (
          <div className="rounded-3xl border border-white/12 bg-white/5 backdrop-blur-md p-8 text-center max-w-md mx-auto" data-testid="readiness-guest-gate">
            <span className="w-14 h-14 rounded-full bg-teal-200/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-6 h-6 text-teal-200/80" />
            </span>
            <p className="text-white/80 text-lg font-light">Your Momentum Engine is personal.</p>
            <p className="text-white/45 text-sm mt-2 leading-relaxed">
              It reads your own private reflections to gently shape effortless goals. Create a free account to unlock it.
            </p>
            <button onClick={() => navigate("/register")} data-testid="readiness-signup"
              className="mt-6 rounded-full px-6 py-3 bg-teal-200/15 border border-teal-200/30 text-white hover:bg-teal-200/25 transition-colors">
              Create your sanctuary
            </button>
          </div>
        )}

        {user && loading && (
          <div className="flex flex-col items-center py-20 text-white/50">
            <div className="w-12 h-12 rounded-full bg-teal-200/20 animate-breathe mb-4" />
            Reading your patterns…
          </div>
        )}

        {!loading && data && (
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-3xl border border-white/12 bg-white/5 p-5" data-testid="drainers">
                <p className="flex items-center gap-2 text-white/80 mb-3"><Zap className="w-4 h-4 text-orange-300" /> Energy drainers</p>
                {(data.drainers || []).map((d, i) => <Chip key={i}>{d}</Chip>)}
              </div>
              <div className="rounded-3xl border border-white/12 bg-white/5 p-5" data-testid="desires">
                <p className="flex items-center gap-2 text-white/80 mb-3"><Sparkles className="w-4 h-4 text-teal-200" /> Hidden core desires</p>
                {(data.desires || []).map((d, i) => <Chip key={i}>{d}</Chip>)}
              </div>
            </div>

            <div>
              <p className="flex items-center gap-2 text-white/70 text-sm mb-3"><Compass className="w-4 h-4" /> Structure portal</p>
              <div className="grid grid-cols-2 gap-3" data-testid="priority-grid">
                {GRID.map((q) => (
                  <div key={q.key} className="rounded-2xl border border-white/12 bg-white/5 p-4">
                    <p className="text-white font-medium text-sm">{q.label}</p>
                    <p className="text-white/40 text-xs mb-2">{q.hint}</p>
                    {((data.grid || {})[q.key] || []).map((it, i) => <Chip key={i}>{it}</Chip>)}
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3" data-testid="micro-goals">
              {[["daily", "Today · 3–5 min", Sun], ["weekly", "This week", CalendarDays], ["yearly", "Horizon", Compass]].map(([k, label, Icon]) => (
                <div key={k} className="rounded-3xl border border-white/12 bg-white/5 p-5">
                  <p className="flex items-center gap-2 text-white/80 mb-3"><Icon className="w-4 h-4 text-teal-200" /> {label}</p>
                  {((data.micro_goals || {})[k] || []).map((g, i) => <Chip key={i}>{g}</Chip>)}
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-center text-white/20 text-xs mt-10 font-light">{quote}</p>
      </div>
    </div>
  );
};

export default Readiness;
